import { ReactElement, useEffect } from 'react';
import { useUINav } from '@/ui';
import { useCompanyContext, useMember } from '@/companies/providers';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../../hooks';
import { useEditProfileAction } from '@/companies/actions';
import {
    MemberAccessSettingsSection,
    MemberActionsSettingsSection,
    MemberPhoneSettingsSection,
    MemberPowerModeSettingsSection,
    MemberProfileSettingsSection,
    MemberTeamsSettingsSection,
    MemberTimezoneSettingsSection
} from '@/companies/components';
import { useFetchMe } from '@/me/queries';

export function MemberIdGeneralSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { company } = useCompanyContext();
    const { companyUser } = useMember();
    const { canEditMembers } = useCompanyPermissions();
    // The member's own details are gated per-member by the API; the sections
    // below it are still company-wide permissions awaiting their own flags.
    const { isAllowed: canEditProfile, reason: editProfileReason } =
        useEditProfileAction({ user: companyUser });

    // Adding a phone is gated on the email of whoever is *doing* the editing,
    // not the user being edited. Already fetched at app init, so this is a
    // cache hit rather than a second request.
    const { data: me } = useFetchMe();

    const name = companyUser.joined
        ? `${companyUser.firstName} ${companyUser.lastName}`
        : companyUser.email;

    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            { text: t('shared.labels.settings') },
            {
                text: t('shared.labels.members'),
                link: `/company/${company.id}/settings/members`
            },
            { text: name },
            { text: t('shared.labels.general') }
        ]);
    }, [setParts, company.id, name, t]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <MemberProfileSettingsSection
                companyUser={companyUser}
                canEdit={canEditProfile}
                editDisabledReason={editProfileReason}
            />
            <MemberPhoneSettingsSection
                companyUser={companyUser}
                canEdit={canEditProfile}
                editDisabledReason={editProfileReason}
                isEmailVerified={me?.user.emailVerified ?? false}
            />
            <MemberAccessSettingsSection companyUser={companyUser} />
            <MemberTeamsSettingsSection
                companyUser={companyUser}
                canEdit={canEditMembers}
            />
            <MemberTimezoneSettingsSection
                companyUser={companyUser}
                canEdit={canEditProfile}
                editDisabledReason={editProfileReason}
            />
            <MemberPowerModeSettingsSection
                companyUser={companyUser}
                canEdit={canEditProfile}
                editDisabledReason={editProfileReason}
            />
            <MemberActionsSettingsSection companyUser={companyUser} />
        </div>
    );
}
